import React, { useState, useEffect, useContext, useMemo } from "react";
import { Grid, SelectPicker } from "rsuite";
import { toast } from "react-toastify";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import { getTaxesAndFees } from "../../../api";
import FilterIcon from "../../../assets/img/filter.svg?react";
import PropertyConfigHeader from "../PropertyConfigHeader";
import PropertyPageCard from "../PropertyPageCard";
import PropertyConfigTable from "../PropertyConfigTable";
import {
  STATUS_FILTER,
  STATUS_FILTER_OPTIONS,
} from "../../../constants/statusFilter";
import {
  toTitleCase,
  formatChargeAmount,
  filterTaxesBySearch,
  filterTaxesByStatus,
} from "./utils";
import "../propertyConfig.scss";
import "./taxesFees.scss";

const columns = [
  {
    key: "name",
    label: "Name",
    className: "taxes-fees-page__col-name",
  },
  {
    key: "isActive",
    label: "Status",
    className: "taxes-fees-page__col-status",
    render: (row) => (
      <span
        className={`taxes-fees-page__status-badge${
          row.isActive ? "" : " taxes-fees-page__status-badge--inactive"
        }`}
      >
        {row.isActive ? "Active" : "Inactive"}
      </span>
    ),
  },
  {
    key: "taxType",
    label: "Type",
    className: "taxes-fees-page__col-type",
    render: (row) => toTitleCase(row.taxType),
  },
  {
    key: "payAtProperty",
    label: "Pay at Property",
    className: "taxes-fees-page__col-pay-at-property",
    render: (row) => (row.payAtProperty ? "Yes" : "No"),
  },
  {
    key: "chargeBasis",
    label: "Charge Basis",
    className: "taxes-fees-page__col-charge-basis",
    render: (row) => toTitleCase(row.chargeBasis),
  },
  {
    key: "chargeFrequency",
    label: "Charge Frequency",
    className: "taxes-fees-page__col-charge-frequency",
    render: (row) => toTitleCase(row.chargeFrequency),
  },
  {
    key: "amount",
    label: "Amount",
    render: (row) => formatChargeAmount(row),
  },
];

const TaxesFees = () => {
  const { selectedProperty } = useContext(PropertyStoreContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER.ALL);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedProperty?.propertyId) return;

    let cancelled = false;
    setTaxes([]);
    setLoading(true);
    setSearchQuery("");
    setStatusFilter(STATUS_FILTER.ALL);

    getTaxesAndFees(selectedProperty.propertyId)
      .then((response) => {
        if (cancelled) return;
        const entity = response?.data?.data?.getEntityWithRelations;
        if (
          entity?.success === false ||
          (entity?.errors && entity.errors.length > 0)
        ) {
          toast.error("Failed to load taxes and fees. Please try again later.");
        } else {
          const taxRelation = entity?.relations?.find(
            (r) => r.entityType === "PROPERTY_TAX"
          );
          setTaxes(taxRelation?.entities || entity?.entity || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load taxes and fees. Please try again later.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProperty?.propertyId]);

  const filteredTaxes = useMemo(
    () =>
      filterTaxesByStatus(
        filterTaxesBySearch(taxes, searchQuery),
        statusFilter
      ),
    [taxes, searchQuery, statusFilter]
  );

  return (
    <Grid fluid className="property-config-page">
      <PropertyConfigHeader title="Taxes & Fees" />

      <PropertyPageCard
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchAriaLabel="Search taxes and fees"
        toolbarLeft={
          <div className="taxes-fees-page__filter-wrapper">
            <FilterIcon className="taxes-fees-page__filter-icon" />
            <SelectPicker
              data={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              searchable={false}
              cleanable={false}
              placeholder="Active & Inactive"
              aria-label="Filter by status"
              className="taxes-fees-page__filter-select"
            />
          </div>
        }
      >
        <PropertyConfigTable
          columns={columns}
          data={filteredTaxes}
          rowKey="id"
          loading={loading}
          loadingMessage="Loading taxes and fees..."
          emptyMessage="No taxes or fees found."
        />
      </PropertyPageCard>
    </Grid>
  );
};

export default TaxesFees;
